import { t } from '@hefe/i18n';
import {
  BasketballTeamsSlice,
  createUserIcalInterface,
  fetchUserIcalInterface,
  userIcalInterfaceSelector,
} from '@hefe/store';
import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { environment } from '../../../../../environments/environment';
import { useAppDispatch } from '../../../../store/store';

export default function UserSettingsInterfacesComponent() {
  const dispatch = useAppDispatch();
  const [loadingIcalInterface, setLoadingIcalInterface] = useState(false);
  const icalInterfaceToken = useSelector(userIcalInterfaceSelector);
  const basketballTeamsSelect = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  const [ownGamesInterfaceSetting, setOwnGamesInterfaceSetting] =
    useState(true);
  const [ownPracticesInterfaceSetting, setOwnPracticesInterfaceSetting] =
    useState(true);
  const [ownRefereeGamesInterfaceSetting, setOwnRefereeGamesInterfaceSetting] =
    useState(true);
  const [ownTasksInterfaceSetting, setOwnTasksInterfaceSetting] =
    useState(true);
  const [
    hideCanceledGamesInterfaceSetting,
    setHideCanceledGamesInterfaceSetting,
  ] = useState(false);
  const [additionalTeamsInterfaceSetting, setAdditionalTeamsInterfaceSetting] =
    useState([]);

  useEffect(() => {
    fetchIcalInterface();
    fetchTeams();
  }, []);

  const fetchIcalInterface = () => {
    setLoadingIcalInterface(true);
    dispatch(
      fetchUserIcalInterface({
        cacheDuration: 20 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => {})
      .finally(() => setLoadingIcalInterface(false));
  };

  const fetchTeams = () => {
    dispatch(
      BasketballTeamsSlice.fetchBasketballTeamsSelect({
        cacheDuration: 20 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => {});
  };

  const generateIcalToken = () => {
    setLoadingIcalInterface(true);
    dispatch(createUserIcalInterface())
      .unwrap()
      .catch(() => {})
      .finally(() => setLoadingIcalInterface(false));
  };

  const iCalInterfaceParameters = useMemo(() => {
    const params: string[] = [];

    if (ownGamesInterfaceSetting) {
      params.push('ownGames=1');
    }
    if (ownPracticesInterfaceSetting) {
      params.push('ownPractices=1');
    }
    if (ownTasksInterfaceSetting) {
      params.push('ownTasks=1');
    }
    if (ownRefereeGamesInterfaceSetting) {
      params.push('ownRefereeGames=1');
    }
    if (hideCanceledGamesInterfaceSetting) {
      params.push('hideOwnCancellations=1');
    }
    if (additionalTeamsInterfaceSetting.length > 0) {
      const teamIds = additionalTeamsInterfaceSetting.join(',');
      params.push('teams=' + teamIds);
    }

    return params.join('&');
  }, [
    additionalTeamsInterfaceSetting,
    hideCanceledGamesInterfaceSetting,
    ownGamesInterfaceSetting,
    ownPracticesInterfaceSetting,
    ownRefereeGamesInterfaceSetting,
    ownTasksInterfaceSetting,
  ]);

  const renderIcalTokenValue = () => {
    if (loadingIcalInterface) {
      return <Spin />;
    }

    if (icalInterfaceToken) {
      return (
        <div>
          <Space direction="vertical">
            <Checkbox
              checked={ownGamesInterfaceSetting}
              onChange={(event) =>
                setOwnGamesInterfaceSetting(event.target.checked)
              }
            >
              {t('settings.interfaces.ical.settings.own_games')}
            </Checkbox>
            <Checkbox
              checked={ownPracticesInterfaceSetting}
              onChange={(event) =>
                setOwnPracticesInterfaceSetting(event.target.checked)
              }
            >
              {t('settings.interfaces.ical.settings.own_practices')}
            </Checkbox>
            <Checkbox
              checked={ownTasksInterfaceSetting}
              onChange={(event) =>
                setOwnTasksInterfaceSetting(event.target.checked)
              }
            >
              {t('settings.interfaces.ical.settings.own_tasks')}
            </Checkbox>
            <Checkbox
              checked={ownRefereeGamesInterfaceSetting}
              onChange={(event) =>
                setOwnRefereeGamesInterfaceSetting(event.target.checked)
              }
            >
              {t('settings.interfaces.ical.settings.own_referee_games')}
            </Checkbox>
            <Checkbox
              checked={hideCanceledGamesInterfaceSetting}
              onChange={(event) =>
                setHideCanceledGamesInterfaceSetting(event.target.checked)
              }
            >
              {t('settings.interfaces.ical.settings.hide_cancellations')}
            </Checkbox>
            <Select
              placeholder={t(
                'settings.interfaces.ical.settings.additional_teams'
              )}
              options={basketballTeamsSelect}
              value={additionalTeamsInterfaceSetting}
              onChange={(values) => setAdditionalTeamsInterfaceSetting(values)}
              allowClear
              mode="multiple"
              style={{ width: '100%' }}
            />
            <Typography.Text style={{ marginTop: 20 }}>
              {environment.serverUrl}/api/v2/user/basketball/ical/
              {icalInterfaceToken.token}?{iCalInterfaceParameters}
            </Typography.Text>
          </Space>
        </div>
      );
    } else {
      return (
        <Button onClick={() => generateIcalToken()}>{t('generate')}</Button>
      );
    }
  };

  return (
    <Row justify="start">
      <Col xs={24} md={12}>
        <Descriptions bordered column={1}>
          <Descriptions.Item
            key={'ical'}
            label={t('settings.interfaces.ical.title')}
          >
            {renderIcalTokenValue()}
          </Descriptions.Item>
        </Descriptions>
      </Col>
    </Row>
  );
}
